import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase-config";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { generateSignedUrl } from "../services/r2Service";
import GenerateMonthlyButton from './GenerateMonthlyButton';
import './AdminDashboard.css';

function formatDateFR(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR');
}

function formatTime(ms) {
  if (!ms || isNaN(ms)) return '0h00';
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.round((ms % 3600000) / 60000);
  return `${hours}h${minutes.toString().padStart(2, '0')}`;
}

function formatTimeHHMM(timestamp) {
  if (!timestamp) return '';
  if (typeof timestamp === 'number') {
    const date = new Date(timestamp * 1000);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  } else if (timestamp.seconds !== undefined) {
    const date = new Date(timestamp.seconds * 1000);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  } else if (timestamp.toDate) {
    const date = timestamp.toDate();
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }
  return '';
}

const AdminTimesheetValidation = () => {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState([]);

  useEffect(() => {
    fetchTimesheets();
  }, [selectedMonth]);

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      console.log('🔍 Chargement des feuilles mensuelles...');

      const snap = await getDocs(collection(db, 'workSessionsMonth'));
      const allTimesheets = [];
      
      snap.forEach(doc => {
        const data = doc.data();
        allTimesheets.push({
          id: doc.id,
          ...data
        });
      });

      console.log(`📊 ${allTimesheets.length} feuille(s) trouvée(s)`);
      
      const sortedTimesheets = allTimesheets.sort((a, b) => {
        const dateA = new Date(a.monthStart);
        const dateB = new Date(b.monthStart);
        return dateB - dateA;
      });

      const months = [...new Set(sortedTimesheets.map(t => `${t.year}-${String(t.monthStart.split('-')[1]).padStart(2, '0')}`))];
      setAvailableMonths(months.sort().reverse());

      let filtered = sortedTimesheets;
      if (selectedMonth === 'current') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const targetMonth = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
        filtered = sortedTimesheets.filter(t => t.monthStart.startsWith(targetMonth));
      } else if (selectedMonth !== 'all') {
        filtered = sortedTimesheets.filter(t => t.monthStart.startsWith(selectedMonth));
      }

      setTimesheets(filtered);
    } catch (error) {
      console.error('❌ Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (timesheetId) => {
    if (!window.confirm('Valider cette feuille de temps ? L\'employé pourra alors la signer.')) {
      return;
    }

    try {
      const timesheetRef = doc(db, 'workSessionsMonth', timesheetId);
      await updateDoc(timesheetRef, {
        status: 'En attente de signature salarié',
        adminValidatedAt: new Date(),
        updatedAt: new Date(),
        timesheetNotificationCreated: true,
        timesheetNotificationVu: false
      });

      console.log('✅ Feuille validée');
      fetchTimesheets();
    } catch (error) {
      console.error('❌ Erreur validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const handleReject = async (timesheetId) => {
    const reason = window.prompt('Raison du refus ?');
    if (!reason) return;

    try {
      const timesheetRef = doc(db, 'workSessionsMonth', timesheetId);
      await updateDoc(timesheetRef, {
        status: 'Refusé',
        rejectionReason: reason,
        adminValidatedAt: new Date(),
        updatedAt: new Date()
      });

      console.log('✅ Feuille refusée');
      fetchTimesheets();
    } catch (error) {
      console.error('❌ Erreur refus:', error);
      alert('Erreur lors du refus');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'En attente de validation Admin': { bg: '#fff3cd', color: '#856404', text: '⏳ En attente' },
      'En attente de signature salarié': { bg: '#d1ecf1', color: '#0c5460', text: '✍️ À signer' },
      'Signé': { bg: '#d4edda', color: '#155724', text: '✅ Signée' },
      'Signée': { bg: '#d4edda', color: '#155724', text: '✅ Signée' },
      'Refusé': { bg: '#f8d7da', color: '#721c24', text: '❌ Refusé' }
    };

    const style = styles[status] || { bg: '#e2e3e5', color: '#383d41', text: status };

    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {style.text}
      </span>
    );
  };

  const getMonthLabel = (monthStr) => {
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const [year, month] = monthStr.split('-');
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const handleDownloadPdf = async (timesheet) => {
    try {
      console.log('📄 Tentative ouverture PDF pour:', timesheet.employeeEmail);
      console.log('📄 Données timesheet:', { 
        pdfFileName: timesheet.pdfFileName, 
        pdfUrl: timesheet.pdfUrl,
        id: timesheet.id
      });

      // Cas 1: On a le pdfFileName
      if (timesheet.pdfFileName) {
        console.log('✅ pdfFileName trouvé, génération URL signée...');
        const signedUrl = await generateSignedUrl(timesheet.pdfFileName);
        console.log('✅ URL générée:', signedUrl);
        window.open(signedUrl, '_blank');
        return;
      }

      // Cas 2: On a l'ancienne pdfUrl
      if (timesheet.pdfUrl) {
        console.log('✅ pdfUrl trouvé, ouverture directe...');
        window.open(timesheet.pdfUrl, '_blank');
        return;
      }

      // Cas 3: Rien trouvé
      alert('❌ Aucun PDF trouvé pour cette feuille de temps');
      
    } catch (error) {
      console.error('❌ Erreur ouverture PDF:', error);
      alert('Erreur: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <h1>Validation des feuilles de temps</h1>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="logo-container">
        <img src="/logo.png" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
      
      <h1>Validation des feuilles de temps mensuelles</h1>

      <GenerateMonthlyButton onSuccess={fetchTimesheets} />

      <div style={{ marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="admin-button" onClick={() => navigate('/admin')}>
          ← Retour
        </button>

        <select 
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '14px'
          }}
        >
          <option value="current">Mois dernier</option>
          <option value="all">Tous les mois</option>
          <optgroup label="Archives">
            {availableMonths.map(month => (
              <option key={month} value={month}>
                {getMonthLabel(month)}
              </option>
            ))}
          </optgroup>
        </select>

        <span style={{ marginLeft: 'auto', color: '#666' }}>
          {timesheets.length} feuille(s)
        </span>
      </div>

      {timesheets.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <p style={{ fontSize: '18px', color: '#666' }}>
            📭 Aucune feuille de temps pour cette période
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            background: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: 12, textAlign: 'left' }}>Employé</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Période</th>
                <th style={{ padding: 12, textAlign: 'center' }}>Sessions</th>
                <th style={{ padding: 12, textAlign: 'center' }}>Temps travail</th>
                <th style={{ padding: 12, textAlign: 'center' }}>Absences</th>
                <th style={{ padding: 12, textAlign: 'center' }}>Statut</th>
                <th style={{ padding: 12, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {timesheets.map((timesheet, idx) => (
                <tr key={timesheet.id} style={{ 
                  borderBottom: '1px solid #dee2e6',
                  background: idx % 2 === 0 ? 'white' : '#f8f9fa'
                }}>
                  <td style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600 }}>{timesheet.employeeName}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{timesheet.employeeEmail}</div>
                  </td>
                  <td style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600 }}>{timesheet.monthName} {timesheet.year}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {formatDateFR(timesheet.monthStart)} - {formatDateFR(timesheet.monthEnd)}
                    </div>
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    {timesheet.sessionCount || 0}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>
                    {formatTime(timesheet.totalWorkTime)}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    {timesheet.absenceCount || 0}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    {getStatusBadge(timesheet.status)}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {/* Bouton Voir seulement si PAS signée */}
                      {timesheet.status !== 'Signée' && (
                        <button
                          onClick={() => navigate(`/admin?employee=${timesheet.employeeEmail}&month=${timesheet.year}-${String(timesheet.monthStart.split('-')[1]).padStart(2, '0')}&timesheetId=${timesheet.id}`)}
                          style={{
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            padding: '6px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          👁️ Voir
                        </button>
                      )}
                      
                      {/* Bouton PDF quand signée */}
                      {timesheet.status === 'Signée' && (
                        <button
                          onClick={() => handleDownloadPdf(timesheet)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '6px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          📄 PDF
                        </button>
                      )}
                      
                      {/* Boutons Valider/Refuser */}
                      {timesheet.status === 'En attente de validation Admin' && (
                        <>
                          <button
                            onClick={() => handleValidate(timesheet.id)}
                            style={{
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '6px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            ✓ Valider
                          </button>
                          <button
                            onClick={() => handleReject(timesheet.id)}
                            style={{
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '6px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            ✗ Refuser
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default AdminTimesheetValidation;